import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Reveal } from "@/hooks/useReveal";
import { ArrowUpRight } from "lucide-react";

const PILLARS = [
  {
    n: "01",
    title: "Discretion",
    body:
      "We move quietly. Off-market introductions, private showings, and confidential negotiations are the rule — not the exception. Many of our most consequential transactions are never publicly listed.",
  },
  {
    n: "02",
    title: "Precision",
    body:
      "Every offer is a calibrated instrument. We build pricing models that combine micro-market data with the irreplaceable judgement that comes from two decades of closings. The result: terms that hold under scrutiny.",
  },
  {
    n: "03",
    title: "Provenance",
    body:
      "Architecture matters. So do the lineage of a property, the integrity of its renovations, and the story it tells. We help clients buy and sell homes that have a place in the conversation — not just the market.",
  },
];

const STATS = [
  { v: "$200M", l: "Total volume sold" },
  { v: "500+", l: "Closings to date" },
  { v: "25+", l: "Years in California" },
  { v: "94%", l: "Sold at or above list" },
];

const APPROACH = [
  {
    title: "Bespoke Marketing",
    body:
      "Editorial photography, architectural film, and curated narrative — every listing is presented to its audience, not just the market. We invest in a property's story before it ever reaches a buyer.",
  },
  {
    title: "Global Network",
    body:
      "Our reach extends from Beverly Hills to international family offices in London, Hong Kong, and the Gulf. Many of our buyers arrive by introduction, not search.",
  },
  {
    title: "Negotiation",
    body:
      "We negotiate as advisors, not adversaries. The objective is the cleanest possible closing — best price, best terms, fewest surprises. Decades of practice show in the details.",
  },
  {
    title: "White-Glove Service",
    body:
      "From the first conversation to the closing, every interaction is handled by a principal. No handoffs, no junior staff, no email queues. You speak with the people doing the work.",
  },
];

const WhyManzano = () => {
  return (
    <main data-testid="why-manzano-page" className="bg-white text-black">
      <Navigation alwaysDark={true} />

      {/* Hero */}
      <section className="pt-32 md:pt-48 pb-20 md:pb-28 px-6 md:px-12">
        <div className="max-w-[1400px] mx-auto">
          <Reveal>
            <p className="overline mb-6">Why Manzano</p>
          </Reveal>
          <Reveal delay={120}>
            <h1 className="font-heading text-6xl md:text-7xl lg:text-[7.5rem] leading-[0.98] tracking-tight max-w-5xl">
              A practice, not a
              <br />
              <em className="italic font-light">brokerage</em>.
            </h1>
          </Reveal>
          <Reveal delay={240}>
            <div className="silver-rule my-12 max-w-2xl" />
          </Reveal>
          <Reveal delay={300}>
            <p className="text-lg md:text-xl text-black/70 leading-relaxed max-w-2xl">
              Manzano Real Estate exists for the client who measures success not in
              square footage but in outcome — the right home, the right price, the
              right moment. Here is how we work.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Our Goal */}
      <section className="bg-[#fafafa] py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="overline mb-6">Our Goal</p>
            </Reveal>
            <Reveal delay={120}>
              <h2 className="font-heading text-5xl md:text-6xl leading-[1.02] tracking-tight">
                Build wealth. Build
                <em className="italic font-light"> legacy</em>.
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={200}>
              <p className="text-base md:text-lg text-black/70 leading-relaxed mb-6">
                Every transaction we touch is part of a longer arc — a portfolio
                being built, a family chapter being written, an estate being
                preserved. We work with that horizon in mind.
              </p>
            </Reveal>
            <Reveal delay={300}>
              <p className="text-base md:text-lg text-black/70 leading-relaxed">
                Our success is measured by the clients who return — and by the
                ones who refer the next generation. After 25+ years, that&apos;s where
                most of our business comes from.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Three Pillars */}
      <section className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-[1400px] mx-auto">
          <Reveal>
            <p className="overline mb-6">The Practice</p>
          </Reveal>
          <Reveal delay={120}>
            <h2 className="font-heading text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight mb-20 max-w-3xl">
              Three principles, every transaction.
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-16">
            {PILLARS.map((p, i) => (
              <Reveal key={p.n} delay={i * 150}>
                <article data-testid={`pillar-${p.n}`}>
                  <p className="font-heading text-[5rem] md:text-[6rem] leading-none text-[#c0c0c0] mb-6">
                    {p.n}
                  </p>
                  <h3 className="font-heading text-3xl md:text-4xl tracking-tight mb-6">
                    {p.title}
                  </h3>
                  <div className="silver-rule mb-6 max-w-[3rem] ml-0" style={{ background: "#c0c0c0" }} />
                  <p className="text-base text-black/70 leading-relaxed">{p.body}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-[#0a0a0a] text-white py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-[1400px] mx-auto">
          <Reveal>
            <p className="overline mb-6 text-[#c0c0c0]">Track Record</p>
          </Reveal>
          <Reveal delay={120}>
            <h2 className="font-heading text-5xl md:text-6xl leading-[1.02] tracking-tight mb-20 max-w-3xl">
              Numbers behind the
              <em className="italic font-light text-[#e8e8e8]"> reputation</em>.
            </h2>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-10 md:gap-12">
            {STATS.map((s, i) => (
              <Reveal key={s.l} delay={i * 100}>
                <div data-testid={`stat-${i}`}>
                  <p className="font-heading text-5xl md:text-6xl lg:text-7xl tracking-tight mb-4">
                    {s.v}
                  </p>
                  <p className="text-[10px] tracking-[0.3em] uppercase text-white/60">
                    {s.l}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Approach */}
      <section className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-[1400px] mx-auto">
          <Reveal>
            <p className="overline mb-6">Approach</p>
          </Reveal>
          <Reveal delay={120}>
            <h2 className="font-heading text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight mb-20 max-w-3xl">
              How a Manzano transaction
              <em className="italic font-light"> actually unfolds</em>.
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-14">
            {APPROACH.map((a, i) => (
              <Reveal key={a.title} delay={i * 100}>
                <article data-testid={`approach-${i}`} className="flex gap-6">
                  <span className="font-heading text-2xl text-[#a9a9a9] shrink-0 w-10">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <h3 className="font-heading text-2xl md:text-3xl tracking-tight mb-4">
                      {a.title}
                    </h3>
                    <p className="text-base text-black/70 leading-relaxed">{a.body}</p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Home valuation CTA */}
      <section className="bg-[#f4f4f4] py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="overline mb-6">Valuation</p>
            </Reveal>
            <Reveal delay={120}>
              <h2 className="font-heading text-5xl md:text-6xl leading-[1.02] tracking-tight">
                Know your home&apos;s
                <em className="italic font-light"> worth</em>.
              </h2>
            </Reveal>
            <Reveal delay={220}>
              <p className="text-base md:text-lg text-black/70 leading-relaxed max-w-xl mt-8">
                A confidential, advisor-prepared market analysis — no automated
                estimates, no public footprint. Delivered within 5 business days.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-5 lg:text-right">
            <Reveal delay={300}>
              <a
                href="https://www.manzanorealestategroup.com/fine/real/estate/marketeval"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="why-cta-valuation"
                className="group inline-flex items-center gap-3 px-10 py-4 bg-black text-white border border-black text-[11px] uppercase tracking-[0.28em] font-medium hover:bg-white hover:text-black transition-all duration-500"
              >
                Request Valuation
                <ArrowUpRight
                  size={16}
                  className="transition-transform duration-500 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                />
              </a>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Work with us */}
      <section className="bg-black text-white py-32 md:py-40 px-6 md:px-12">
        <div className="max-w-[1400px] mx-auto text-center">
          <Reveal>
            <p className="overline mb-6 text-[#c0c0c0]">Work With Us</p>
          </Reveal>
          <Reveal delay={120}>
            <h2 className="font-heading text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight mb-12 max-w-4xl mx-auto">
              The next conversation
              <em className="italic font-light text-[#e8e8e8]"> starts here</em>.
            </h2>
          </Reveal>
          <Reveal delay={220}>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/#contact"
                data-testid="why-cta-contact"
                className="inline-flex items-center justify-center px-12 py-4 bg-white text-black border border-white text-[11px] uppercase tracking-[0.28em] font-medium hover:bg-transparent hover:text-white transition-all duration-500"
              >
                Begin a Conversation
              </Link>
              <Link
                to="/#listings"
                data-testid="why-cta-listings"
                className="inline-flex items-center justify-center px-12 py-4 bg-transparent text-white border border-white/60 text-[11px] uppercase tracking-[0.28em] font-medium hover:bg-white hover:text-black transition-all duration-500"
              >
                View Portfolio
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default WhyManzano;
